import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import GeoSearchAutocomplete from "@/components/GeoSearchAutocomplete";
import { FlyTo } from "@/components/MapCore";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Camera, Loader2, X, Tag, Gift, Recycle, ArrowLeft } from "lucide-react";
import { TYPE_META } from "@/pages/MarketplacePage";
import { labelCat, labelSub, labelItem } from "@/lib/marketplaceLabels";
import VideoUploader from "@/components/VideoUploader";
import { useMarketplaceRulesGate } from "@/components/MarketplaceRulesGate";

function ClickToSet({ onSet }) {
  useMapEvents({ click: (e) => onSet([e.latlng.lat, e.latlng.lng]) });
  return null;
}

// Explanation keys shown on the mode-choice screen (first step). Resolved
// via t() at render time so mode hints follow the language selector.
const MODE_HINT_KEY = { sale: "mkc_hint_sale", gift: "mkc_hint_gift", reusable: "mkc_hint_reusable" };
const MODE_TITLE_KEY = { sale: "what_are_you_selling", gift: "what_are_you_giving", reusable: "what_did_you_find" };
const MODE_SUBTITLE_KEY = { sale: "mkc_subtitle_sale", gift: "mkc_subtitle_gift", reusable: "mkc_subtitle_reusable" };

export default function MarketplaceCreate() {
  const { user, loading } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const { requireMarketplaceRules, gateElement } = useMarketplaceRulesGate();
  // Read initial mode from ?type= if present (e.g. from tab shortcut); otherwise force choice.
  const initialType = (() => {
    const p = new URLSearchParams(loc.search).get("type");
    return ["sale", "gift", "reusable"].includes(p) ? p : null;
  })();
  const [type, setType] = useState(initialType); // null until user chooses
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [taxonomy, setTaxonomy] = useState({});
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [itemType, setItemType] = useState("");
  const [condition, setCondition] = useState("good");
  const [color, setColor] = useState("");
  const [keywords, setKeywords] = useState("");
  const [price, setPrice] = useState("");
  const [negotiable, setNegotiable] = useState(false);
  const [pickup, setPickup] = useState(true);
  const [shipping, setShipping] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);  // Phase 4
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pos, setPos] = useState(null);
  // Optional territorial pre-selection (Paese / Provincia / Comune).
  // Picking a territory flies the map to its centroid; the user can then
  // fine-tune by clicking on the map. The precise pin (pos) is what gets sent.
  const [geoArea, setGeoArea] = useState(null);
  const [flyCenter, setFlyCenter] = useState(null);

  useEffect(() => { if (!loading && !user) nav("/auth?mode=signin"); }, [user, loading, nav]);
  // Marketplace publishing is 18+ only. Bounce minors back to the read-only
  // marketplace list — the backend will 403 the POST anyway.
  useEffect(() => {
    if (!loading && user && user.is_adult === false) {
      toast.info(t("mk_adult_only_button_tip"));
      nav("/marketplace");
    }
  }, [user, loading, nav, t]);
  useEffect(() => { api.get("/marketplace/taxonomy").then((r) => setTaxonomy(r.data || {})).catch(() => {}); }, []);

  const subOptions = category && taxonomy[category] ? Object.keys(taxonomy[category]) : [];
  const itemOptions = subcategory && taxonomy[category]?.[subcategory] ? taxonomy[category][subcategory] : [];

  const uploadPhoto = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, data.path]);
    } catch (e) { toast.error(e.response?.data?.detail || t("mkc_upload_failed")); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!title.trim()) return toast.error(t("mkc_err_title_required"));
    if (!category || !subcategory || !itemType) return toast.error(t("mkc_err_taxonomy_required"));
    if (!pos) return toast.error(t("mkc_err_position_required"));
    if (type === "sale" && (!price || Number(price) < 0)) return toast.error(t("mkc_err_price_required"));
    // iter80 gate: adult users must accept Marketplace Rules v1.0 before their
    // first operational action. If already accepted the modal is skipped.
    const ok = await requireMarketplaceRules();
    if (!ok) return;
    setSaving(true);
    try {
      const payload = {
        type, title: title.trim(), description: description.trim(),
        photos, videos, category, subcategory, item_type: itemType,
        condition, color: color || null,
        keywords: keywords.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 20),
        price: type === "sale" ? Number(price) : null,
        negotiable: type === "sale" ? negotiable : false,
        lat: pos[0], lng: pos[1],
        pickup: type === "reusable" ? true : pickup,
        shipping: type === "reusable" ? false : shipping,
      };
      const { data } = await api.post("/marketplace/listings", payload);
      toast.success(t("mkc_success_published"));
      nav(`/marketplace/${data.listing_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || t("mkc_err_generic")); }
    finally { setSaving(false); }
  };

  // ─── STEP 1: MODE CHOICE ─────────────────────────────────────────────
  if (!type) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]" data-testid="mk-create-page">
        <AppHeader />
        <div className="max-w-4xl mx-auto px-4 md:px-8 pt-6 pb-4"><BackButton /></div>
        <div className="max-w-4xl mx-auto px-4 md:px-8 pb-12">
          <div className="text-xs uppercase tracking-[0.25em] text-slate-600 font-semibold mb-1">{t("mkc_new_listing_kicker")}</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-testid="mkc-mode-heading">
            {t("mkc_what_type_heading")}
          </h1>
          <p className="text-sm text-slate-600 mb-6 max-w-2xl">
            {t("mkc_type_choice_desc")}
          </p>
          <div className="grid md:grid-cols-3 gap-3" data-testid="mkc-mode-picker">
            {Object.entries(TYPE_META).map(([k, m]) => {
              const Icon = m.icon;
              return (
                <button
                  key={k}
                  type="button"
                  data-testid={`mkc-mode-choose-${k}`}
                  onClick={() => setType(k)}
                  className="text-left rounded-2xl border-2 p-5 bg-white hover:shadow-md transition-shadow"
                  style={{ borderColor: m.color }}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-3 text-white"
                    style={{ background: m.color }}
                  >
                    <Icon size={22} />
                  </div>
                  <div className="text-lg font-bold text-slate-900 mb-1">{t(`listing_type_${k}`)}</div>
                  <div className="text-xs text-slate-600 leading-snug">{t(MODE_HINT_KEY[k])}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ─── STEP 2: FORM ────────────────────────────────────────────────────
  const TypeIcon = TYPE_META[type].icon;
  const dynTitle = t(MODE_TITLE_KEY[type]);
  const dynSubtitle = t(MODE_SUBTITLE_KEY[type]);

  const changeMode = (k) => {
    setType(k);
    // Reset mode-specific fields when switching to avoid stale data.
    setPrice("");
    setNegotiable(false);
    if (k === "reusable") { setPickup(true); setShipping(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="mk-create-page">
      <AppHeader />
      <div className="max-w-4xl mx-auto px-4 md:px-8 pt-6 pb-4"><BackButton /></div>
      <div className="max-w-4xl mx-auto px-4 md:px-8 pb-12">
        <button
          type="button"
          data-testid="mkc-mode-back"
          onClick={() => setType(null)}
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 mb-2"
        >
          <ArrowLeft size={12} /> {t("change_type")}
        </button>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-600 font-semibold mb-1 inline-flex items-center gap-1 ml-2">
          <TypeIcon size={12} /> Marketplace · <span style={{ color: TYPE_META[type].color }}>{t(`listing_type_${type}`)}</span>
        </div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-1" data-testid="mkc-dynamic-title">{dynTitle}</h1>
        <div className="text-sm text-slate-600 mb-4" data-testid="mkc-dynamic-subtitle">{dynSubtitle}</div>

        <div className="mb-4 flex flex-wrap gap-2">
          {Object.entries(TYPE_META).map(([k, m]) => {
            const Icon = m.icon;
            return (
              <button
                key={k}
                data-testid={`mkc-type-${k}`}
                onClick={() => changeMode(k)}
                className="h-9 px-3 rounded-full text-xs font-semibold border inline-flex items-center gap-1"
                style={{ background: type===k?m.color:'#fff', borderColor: type===k?m.color:'#e5e7eb', color: type===k?'#fff':'#334155' }}
              >
                <Icon size={13} /> {t(`listing_type_${k}`)}
              </button>
            );
          })}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("mkc_title_label")} *</label>
              <Input data-testid="mkc-title" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-xl mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("mkc_description_label")}</label>
              <Textarea data-testid="mkc-desc" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-xl mt-1" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("mkc_category_label")} *</label>
                <Select value={category} onValueChange={(v) => { setCategory(v); setSubcategory(""); setItemType(""); }}>
                  <SelectTrigger data-testid="mkc-cat" className="rounded-full mt-1"><SelectValue placeholder={t("mkc_choose_placeholder")} /></SelectTrigger>
                  <SelectContent>{Object.keys(taxonomy).map((c) => <SelectItem key={c} value={c} data-testid={`mkc-cat-opt-${c}`}>{labelCat(c, lang)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("mkc_subcategory_label")} *</label>
                <Select value={subcategory} onValueChange={(v) => { setSubcategory(v); setItemType(""); }} disabled={!category}>
                  <SelectTrigger data-testid="mkc-sub" className="rounded-full mt-1"><SelectValue placeholder={category ? t("mkc_choose_placeholder") : t("category_first_short")} /></SelectTrigger>
                  <SelectContent>{subOptions.map((s) => <SelectItem key={s} value={s} data-testid={`mkc-sub-opt-${s}`}>{labelSub(s, lang)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("mkc_itemtype_label")} *</label>
                <Select value={itemType} onValueChange={setItemType} disabled={!subcategory}>
                  <SelectTrigger data-testid="mkc-item" className="rounded-full mt-1"><SelectValue placeholder={subcategory ? t("mkc_choose_placeholder") : t("mkc_subcategory_first")} /></SelectTrigger>
                  <SelectContent>{itemOptions.map((i) => <SelectItem key={i} value={i} data-testid={`mkc-item-opt-${i}`}>{labelItem(i, lang)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {type !== "reusable" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("mkc_condition_label")}</label>
                  <Select value={condition} onValueChange={setCondition}>
                    <SelectTrigger data-testid="mkc-cond" className="rounded-full mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">{t("mkc_condition_new")}</SelectItem>
                      <SelectItem value="like_new">{t("condition_like_new")}</SelectItem>
                      <SelectItem value="good">{t("mkc_condition_good")}</SelectItem>
                      <SelectItem value="used">{t("mkc_condition_used")}</SelectItem>
                      <SelectItem value="fair">{t("condition_fair")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("mkc_color_label")}</label>
                  <Input data-testid="mkc-color" value={color} onChange={(e) => setColor(e.target.value)} placeholder={t("mkc_color_placeholder")} className="rounded-full mt-1" />
                </div>
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("mkc_keywords_label")}</label>
              <Input data-testid="mkc-keywords" value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder={t("mkc_keywords_placeholder")} className="rounded-full mt-1" />
            </div>
            {type === "sale" && (
              <div className="grid grid-cols-2 gap-2 items-end" data-testid="mkc-sale-price-block">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("mkc_price_label")} *</label>
                  <Input data-testid="mkc-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="rounded-full mt-1" />
                </div>
                <label className="text-xs font-semibold flex items-center gap-2 h-9">
                  <Checkbox data-testid="mkc-negotiable" checked={negotiable} onCheckedChange={(v) => setNegotiable(!!v)} /> {t("mkc_negotiable")}
                </label>
              </div>
            )}
            {type !== "reusable" && (
              <div className="flex gap-4">
                <label className="text-xs font-semibold flex items-center gap-2">
                  <Checkbox data-testid="mkc-pickup" checked={pickup} onCheckedChange={(v) => setPickup(!!v)} /> {t("mkc_pickup")}
                </label>
                <label className="text-xs font-semibold flex items-center gap-2">
                  <Checkbox data-testid="mkc-shipping" checked={shipping} onCheckedChange={(v) => setShipping(!!v)} /> {t("mkc_shipping")}
                </label>
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("mkc_photos_label")}</label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {photos.map((p, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setPhotos((ps) => ps.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X size={12} /></button>
                  </div>
                ))}
                <label data-testid="mkc-photo-add" className="aspect-square rounded-lg border-2 border-dashed border-emerald-300 flex flex-col items-center justify-center cursor-pointer hover:bg-emerald-50">
                  {uploading ? <Loader2 className="animate-spin" size={16} /> : <Camera size={16} />}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ""; }} />
                </label>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("video_upload_label")}</label>
              <VideoUploader value={videos} onChange={setVideos} testidPrefix="mkc-video" />
            </div>
            <Button data-testid="mkc-submit" onClick={submit} disabled={saving} className="w-full h-11 rounded-full text-white hover:text-white" style={{ background: TYPE_META[type].color }}>
              {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : null} {t("publish_listing")}
            </Button>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{t("mkc_position_label")} *</div>
            <div className="text-xs text-slate-600 mb-2">{t("mkc_position_hint")}</div>
            <div className="mb-2">
              <GeoSearchAutocomplete
                testid="mkc-geo-search"
                value={geoArea}
                onChange={(area) => {
                  setGeoArea(area);
                  if (area && typeof area.lat === "number" && typeof area.lng === "number") {
                    setPos([area.lat, area.lng]);
                    setFlyCenter([area.lat, area.lng]);
                  }
                }}
                placeholder={t("country_or_province")}
              />
              {geoArea && (
                <div className="mt-1 text-[10px] text-emerald-800 font-semibold" data-testid="mkc-geo-selected">
                  {t("mkc_territory_prefix")}: {geoArea.display}
                </div>
              )}
            </div>
            <div className="rounded-2xl overflow-hidden border-2 border-slate-200 h-[480px]" data-testid="mkc-map">
              <MapContainer center={[42, 12]} zoom={5} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
                <ClickToSet onSet={setPos} />
                <FlyTo center={flyCenter} zoom={11} />
                {pos && <Marker position={pos} icon={new L.DivIcon({
                  className: "",
                  html: `<div style="width:22px;height:22px;background:${TYPE_META[type].color};border:3px solid #fff;border-radius:50%"></div>`,
                  iconSize: [22, 22], iconAnchor: [11, 11],
                })} />}
              </MapContainer>
            </div>
          </div>
        </div>
      </div>
      {gateElement}
    </div>
  );
}
